import React, { useEffect, useState} from 'react';
import { StudyModalInput, StudyModalOutput, StudyModalParameter } from './';

export interface IColorColor {
	color: string,
	pattern: "solid" | "dashed" | "dotted"
	width: number
}

interface IStudyDescriptor {
	chart: any
	customLegend?: any
	editFunction: (forceEdit: boolean) => void
	error: any
	extendToEnd?: any
	highlight: string
	inputs: object
	libraryEntry: ILibraryEntry
	max?: number
	min?: number
	name: string
	noSlopes?: boolean
	outputMap: object
	outputs: object
	overlay: boolean
	panel: string
	parameters: {
		chartName: string,
		editMode: boolean
	}
	permanent: undefined
	prev: string
	startFrom: 0
	study: ILibraryEntry
	subField: string
	type: string
}

interface ILibraryEntry {
	inputs: object
	name: string
	outputs: object
	overlay: boolean
	seriesFN: (stx: any, sd: any, quotes: any) => void
	useRawValues: boolean
}

interface IStudyDialogHelper {
	attributes: object
	axisSelect?: any
	dateTimeInputs: any[]
	inputs: any[]
	libraryEntry: ILibraryEntry
	name: string
	outputs: IInputOutput[]
	panelSelect?: any
	parameters: any[]
	sd: IStudyDescriptor
	stx: any
	title: string
}

interface IStudyModalProps {
	studyHelper: IStudyDialogHelper,
	showStudyModal: boolean,
	closeStudyModal: () => void
	updateStudyAndSave: (inputs: IInputOutput, outputs: IInputOutput, params: IParams) => void
}

export interface IParams {
	heading?: string,
	color?: string | IColorColor,
	value?: any,
	name?: string,
}

export interface IInputOutput extends IParams {
	heading?: string,
	type?: string,
	options?: object,
	// defaultOutputs
}

export const StudyModal: React.FunctionComponent<IStudyModalProps> = ({ updateStudyAndSave, closeStudyModal, showStudyModal, studyHelper }) => {
	const [inputs, setInputs] = useState<IInputOutput[]>()
	const [outputs, setOutputs] = useState<IInputOutput[]>()
	const [parameters, setParameters] = useState<IParams[]>()

	useEffect(() => {
		if (studyHelper) {
			setInputs(studyHelper.inputs)
			setOutputs(studyHelper.outputs)
			setParameters(studyHelper.parameters)
		}
	}, [studyHelper])

	const setColor = (color: string | IColorColor, type: string, name: string | undefined): void => {
		if (type === 'output') {
			if (outputs) {
				outputs.forEach(output => {
					if (output.name === name) output.color = `#${color}`;
				});
			}
		} else if (type === 'input') {
			if (inputs) {
				inputs.forEach(input => {
					if (input.name === name) input.color = `#${color}`;
				});
			}
		}
	}

	const updateStudy = () => {
		const currentInputs: IInputOutput = {};
		const currentOutputs: IInputOutput = {};
		const currentParams: IParams = {};
		if (inputs) {
			inputs.forEach(input => { if (input.name) { currentInputs[input.name] = input.value }})
		}
		if (outputs) {
			outputs.forEach(output => { if (output.name) { currentOutputs[output.name] = output.color }})
		}
		if (parameters) {
			parameters.forEach(param => {
				currentParams[`${param.name}Value`] = param.value
				currentParams[`${param.name}Color`] = param.color
			})
		}

		updateStudyAndSave(currentInputs, currentOutputs, currentParams);
	}

	const updateInputs = (name: string, value: string | number | boolean) => {
		if (inputs) {
			inputs.forEach(input => {
				if (input.name === name) input.value = value;
			})
		}
  }

	if (!showStudyModal || !studyHelper) { return <span /> }

	const inputsElements = (inputs || []).map((input, i) => (<StudyModalInput key={'input' + i} input={input} updateInputs={updateInputs} />));
	const outputsElements = (outputs || []).map((output, i) => (<StudyModalOutput key={'output' + i} output={output} setColor={setColor} />));
	const paramsElements = (parameters || []).map((param, i) => (<StudyModalParameter setColor={setColor} key={'param' + i} param={param} />));

	return (
		<div className="dialog-overlay" id="studyDialog">
			<div className="dialog">
				<div className="cq-close" onClick={closeStudyModal} />
				<div className="dialog-heading">
					{studyHelper.name}
				</div>
				<div id="inputs">
					{inputsElements}
				</div>
				<div id="outputs">
					{outputsElements}
				</div>
				<div id="parameters">
					<div className="parameters dialog-item">
						{paramsElements}
					</div>
				</div>
				<button className="pull-right" onClick={updateStudy}>Save</button>
				<div className="clearFloat" />
			</div>
		</div>
	);
}
